import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { ApiConfiguration } from '../api-configuration';
import { BaseService } from '../base-service';
import { RequestBuilder } from '../request-builder';
import { StrictHttpResponse } from '../strict-http-response';
import { ReportsResponse } from '@api/index';
import {
  PeopleCounterStatisticsResponse,
  VisitorsPerHour,
  AgeDistributionResponse,
  PeopleTypeData,
  AgeAndGenderDistributionResponse,
  WeatherPerHourResponse,
  DailyExposuresAndWeather,
  ExposuresRequest,
  ExposuresTotalsResponse,
  ExposuresAverageResponse,
  ExposuresMediaStats,
  ImpressionsVsBudgetedImpressionsResponse,
  ExposuresVsBudgetedExposuresResponse
} from '@admin/statistics/statistics.model';
import { ExposuresReportsRequest } from '../models/exposures-reports-request';
import { PeopleCounterReportsRequest } from '../models/people-counter-reports-request';
import { PeopleCounterRequest } from '../models/people-counter-request';
import { SmsStatisticsResponse } from '../models/sms-statistics-response';
import { StatisticsEngagementMediaFilesRequest } from '../models/statistics-engagement-media-files-request';
import { StatisticsEngagementMediaFilesResponse } from '../models/statistics-engagement-media-files-response';
import { ReachReportsRequest } from '@api/models/reach-reports-request';
import { VehicleTypeResponse } from '@app/admin/statistics/reach/reach.models';
import { IdName } from '@api/models/idname';
import { MoodDataResponse } from '@app/admin/statistics/people-count/components/mood-chart/mood-chart.models';
import { DeviceOptions, DeviceRequest } from '@app/shared/models/deviceOptions';
import { EngagementRequest } from '@api/models/engagement-request';
import { EngagementResponse } from '@api/models/engagement';
import { ManualSendoutRequest } from '@app/admin/statistics/views/sendout-model';
import { CampaignEmailSendout } from '@app/admin/statistics/views/views.model';
import { ActiveScreenOrGroupParameters, ScreenOrGroupListResponse } from '@admin/screen/model/screen-group.model';
import { PaginationResult } from '@api/models/paginator';
import { DevicesOnlyRequest } from '@admin/screen/device-reports/device-reports.model';

@Injectable({
  providedIn: 'root',
})
export class StatisticsApi extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  static readonly GetStatisticsPath = '/statistics';

  getVisitorsPerHour(params): Observable<VisitorsPerHour[]> {
    return this.http.post<VisitorsPerHour[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/visitors-per-hour`, params);
  }

  getAgeDistribution(params): Observable<AgeDistributionResponse> {
    return this.http.post<AgeDistributionResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/age-distribution`, params);
  }

  getGenderDistribution(params): Observable<PeopleTypeData> {
    return this.http.post<PeopleTypeData>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/gender-distribution`, params);
  }

  getPOIReports(params?) {
    let body = {};
    if (params) {
      body = {
        params: {
          StartDate: params.startDate?.toISODate?.() || params.startDate || '',
          EndDate: params.endDate?.toISODate?.() || params.endDate || '',
          PersonOfInterestIds: params.poiName || [],
          AdministrativeInclusion: params.includePOIAdministrativeDetails,
          SMSNotificationDetailsInclusion: params.includeSMSNotifications,
          ScreenIds: params.screenIds,
          CategoryIds: params.poiCategoryName,
          CustomerIds: params.customerIds,
          pageNumber: params.pageNumber,
          pageSize: params.pageSize,
          sortBy: params.sortBy,
          sortDirection: params.sortDirection,
        },
      };
    }
    return this.http.get<ReportsResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/personsOfInterestStatistics`, body);
  }

  downloadPOIReports(body?) {
    const bodyToSend = {
      startDate: body.startDate,
      endDate: body.endDate,
      personOfInterestIds: body.poiName,
      customerIds: body.customerIds,
      administrativeInclusion: body.includePOIAdministrativeDetails,
      smsNotificationDetailsInclusion: body.includeSMSNotifications,
      screenIds: body.screenIds,
      categoryIds: body.poiCategoryName,
      pageNumber: body.pageNumber,
      pageSize: body.pageSize,
      sortBy: body.sortBy,
      sortDirection: body.sortDirection,
    };

    return this.http.post(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/downloadPersonsOfInterestReports`, bodyToSend, {
      responseType: 'blob',
    });
  }

  getSmsStatistics(params) {
    let body = {};
    if (params) {
      body = {
        params: {
          customerId: params.customerId || '',
          startDate: params.startDate || '',
          endDate: params.endDate || '',
        },
      };
    }
    return this.http.get<SmsStatisticsResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/sms`, body);
  }

  getWeather(params): Observable<WeatherPerHourResponse> {
    return this.http.get<WeatherPerHourResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/weather-for-channel`, { params });
  }

  getReachWeatherPerDay(params): Observable<DailyExposuresAndWeather[]> {
    return this.http.post<DailyExposuresAndWeather[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/reach-weather-per-day`, params);
  }

  getMoods(params): Observable<MoodDataResponse> {
    return this.http.get<MoodDataResponse>(`${this.rootUrl}/statistics/mood-tracking-per-day`, { params });
  }

  getVehicles(params): Observable<VehicleTypeResponse> {
    return this.http.post<any>(`${this.rootUrl}/statistics/reach/vehicles`, params);
  }

  getVehiclesWeatherPerDay(params): Observable<DailyExposuresAndWeather[]> {
    return this.http.post<DailyExposuresAndWeather[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/reach/vehicles-weather-per-day`, params);
  }

  static readonly GetAdvertisementStatisticsStatisticsPath = '/statistics/advertisements/{advertisementId}';

  getAdvertisementStatistics$Response(params: {
    advertisementId: number;
    body?: StatisticsEngagementMediaFilesRequest;
  }): Observable<StrictHttpResponse<StatisticsEngagementMediaFilesResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.GetAdvertisementStatisticsStatisticsPath, 'post');
    if (params) {
      rb.path('advertisementId', params.advertisementId, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<StatisticsEngagementMediaFilesResponse>;
        })
      );
  }

  getAdvertisementStatistics(params: {
    advertisementId: number;
    body?: StatisticsEngagementMediaFilesRequest;
  }): Observable<StatisticsEngagementMediaFilesResponse> {
    return this.getAdvertisementStatistics$Response(params).pipe(
      map((r: StrictHttpResponse<StatisticsEngagementMediaFilesResponse>) => r.body as StatisticsEngagementMediaFilesResponse)
    );
  }

  static readonly GetPlaylistMediaSettingsStatisticsStatisticsPath = '/statistics/playlistMediaFiles/{playlistMediaFileId}';

  getPlaylistMediaSettingsStatistics$Response(params: {
    playlistMediaFileId: number;
    body?: StatisticsEngagementMediaFilesRequest;
  }): Observable<StrictHttpResponse<StatisticsEngagementMediaFilesResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.GetPlaylistMediaSettingsStatisticsStatisticsPath, 'post');
    if (params) {
      rb.path('playlistMediaFileId', params.playlistMediaFileId, {});
      rb.body(params.body, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<StatisticsEngagementMediaFilesResponse>;
        })
      );
  }

  getPlaylistMediaSettingsStatistics(params: {
    playlistMediaFileId: number;
    body?: StatisticsEngagementMediaFilesRequest;
  }): Observable<StatisticsEngagementMediaFilesResponse> {
    return this.getPlaylistMediaSettingsStatistics$Response(params).pipe(
      map((r: StrictHttpResponse<StatisticsEngagementMediaFilesResponse>) => r.body as StatisticsEngagementMediaFilesResponse)
    );
  }

  getEngagementMediaFiles(params?): Observable<any> {
    return this.http.get(`${this.rootUrl}/statistics/engagements-media-files`, { params });
  }

  static readonly PeopleCounterReportsStatisticsPath = '/statistics/people-counter-report';

  peopleCounterReports$Response(params?: { body?: PeopleCounterReportsRequest }): Observable<StrictHttpResponse<ReportsResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.PeopleCounterReportsStatisticsPath, 'post');
    if (params) {
      rb.body({ ...params.body, ...params }, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ReportsResponse>;
        })
      );
  }

  getPeopleCounterReports(params?: { body?: PeopleCounterReportsRequest }): Observable<ReportsResponse> {
    return this.peopleCounterReports$Response(params).pipe(map((r: StrictHttpResponse<ReportsResponse>) => r.body as ReportsResponse));
  }

  static readonly DownloadPeopleCounterReportsStatisticsPath = '/statistics/download-people-counter-report';

  downloadPeopleCounterReports$Response(params?: { body?: PeopleCounterReportsRequest }): Observable<StrictHttpResponse<ReportsResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.DownloadPeopleCounterReportsStatisticsPath, 'post');
    if (params) {
      rb.body({ ...params.body, ...params }, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<any>;
        })
      );
  }

  downloadPeopleCounterReports(params?: { body?: PeopleCounterReportsRequest }): Observable<any> {
    return this.downloadPeopleCounterReports$Response(params).pipe(map((r: StrictHttpResponse<any>) => r.body as any));
  }

  static readonly DownloadReachReportsStatisticsPath = '/statistics/download-reach-report';

  downloadReachReports$Response(params?: { body?: ReachReportsRequest }): Observable<StrictHttpResponse<ReportsResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.DownloadReachReportsStatisticsPath, 'post');
    if (params) {
      rb.body({ ...params.body, ...params }, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<any>;
        })
      );
  }

  downloadReachReports(params?: { body?: ReachReportsRequest }): Observable<any> {
    return this.downloadReachReports$Response(params).pipe(map((r: StrictHttpResponse<any>) => r.body as any));
  }
  static readonly DownloadExposuresReportsStatisticsPath = '/statistics/download-exposures-reports';

  downloadExposuresReports$Response(params?: { body?: ExposuresReportsRequest }): Observable<StrictHttpResponse<ReportsResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.DownloadExposuresReportsStatisticsPath, 'post');
    if (params) {
      rb.body({ ...params.body, ...params }, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'blob',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<any>;
        })
      );
  }

  downloadExposuresReports(params?: { body?: ExposuresReportsRequest }): Observable<any> {
    return this.downloadExposuresReports$Response(params).pipe(map((r: StrictHttpResponse<any>) => r.body as any));
  }

  static readonly ExposuresReportsStatisticsPath = '/statistics/exposures-report';

  exposuresReports$Response(params?: { body?: ExposuresReportsRequest }): Observable<StrictHttpResponse<ReportsResponse>> {
    const rb = new RequestBuilder(this.rootUrl, StatisticsApi.ExposuresReportsStatisticsPath, 'post');
    if (params) {
      rb.body({ ...params.body, ...params }, 'application/*+json');
    }

    return this.http
      .request(
        rb.build({
          responseType: 'json',
          accept: 'text/json',
        })
      )
      .pipe(
        filter((r: any) => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ReportsResponse>;
        })
      );
  }

  getExposuresReports(params?: { body?: ExposuresReportsRequest }): Observable<ReportsResponse> {
    return this.exposuresReports$Response(params).pipe(map((r: StrictHttpResponse<ReportsResponse>) => r.body as ReportsResponse));
  }

  getStatisticsCategories(params): Observable<any> {
    return this.http.get(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/categories`, {
      params,
    });
  }

  getStatisticsPersonsOfInterest(params): Observable<any> {
    return this.http.get(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/personsOfInterest`, {
      params,
    });
  }

  getPeopleCounterStatistics(params): Observable<PeopleCounterStatisticsResponse> {
    return this.http.post<PeopleCounterStatisticsResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/people-counter`, params);
  }

  reachPerDay(params?: PeopleCounterRequest): Observable<AgeAndGenderDistributionResponse> {
    return this.http.post<AgeAndGenderDistributionResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/reach`, params);
  }

  // POST/statistics/media-files/exposure-totals Gets the total exposure metrics for campaign media files
  getTotalNumberOfExposures(params: ExposuresRequest): Observable<ExposuresTotalsResponse> {
    return this.http.post<ExposuresTotalsResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/media-files/exposure-totals`, params);
  }

  // POST/statistics/media - files / average Gets the total exposure metrics for campaign media files
  getAverageExposures(params: ExposuresRequest): Observable<ExposuresAverageResponse> {
    return this.http.post<ExposuresAverageResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/media-files/average`, params);
  }

  // POST/statistics/media - files / attention - time - average - per - hour
  getAttentionTimeAveragePerHour(params: ExposuresRequest): Observable<ExposuresAverageResponse> {
    return this.http.post<ExposuresAverageResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/media-files/attention-time-average-per-hour`, params);
  }

  // POST/statistics/media - files / exposures Generates exposure statistics
  getExposures(params: ExposuresRequest): Observable<ExposuresMediaStats> {
    return this.http.post<ExposuresMediaStats>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/media-files/kpis`, params);
  }

  getBookingMediaFilesImpressionsComparison(params: ExposuresRequest): Observable<ImpressionsVsBudgetedImpressionsResponse> {
    return this.http.put<ImpressionsVsBudgetedImpressionsResponse>(
      `${this.rootUrl}${StatisticsApi.GetStatisticsPath}/media-files/impressions-comparison`,
      params
    );
  }

  getBookingMediaFilesExposuresComparison(params: ExposuresRequest): Observable<ExposuresVsBudgetedExposuresResponse> {
    return this.http.put<ExposuresVsBudgetedExposuresResponse>(
      `${this.rootUrl}${StatisticsApi.GetStatisticsPath}/media-files/exposures-comparison`,
      params
    );
  }

  getExposuresWeatherPerDay(params): Observable<ExposuresAverageResponse[]> {
    return this.http.post<ExposuresAverageResponse[]>(`${this.rootUrl}/statistics/exposures-weather-per-day`, params);
  }

  averageTimePerHour(params): Observable<any> {
    return this.http.get(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/average-time-per-hour`, { params });
  }

  getPlacements(channelIds: number[]): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/placements`, { params: { channelIds } });
  }

  getFilteredScreensByPlacements(
    placementIds: number[],
    trackingTypes: number[],
    deviceTypesForFiltering: number[],
    channelIds: number[]
  ): Observable<any> {
    const params: any = {
      placementIds,
      trackingTypes,
      deviceTypesForFiltering,
      channelIds,
    };
    return this.http.post(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/filter-screens-by-placements`, params);
  }

  getTags(customerIds: any[]): Observable<any> {
    return this.http.get(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/tags`, { params: { customerIds } });
  }

  getContactPersonsForCustomer(params): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/contact-persons`, { params: params });
  }

  getScreensWithOrientation(params) {
    return this.http.get(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/screenGroups/screens`, { params });
  }

  getReachReports(body) {
    return this.http.post(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/reach-report`, body);
  }

  getChannelForStatistics(customerIds: number[]): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/channels`, { params: { customerIds } });
  }

  getForecastEnabledChannels(customerIds: number[]): Observable<IdName[]> {
    return this.http.get<IdName[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/forecast-enabled-channels`, { params: { customerIds } });
  }

  getDevices(body: DeviceRequest): Observable<DeviceOptions[]> {
    return this.http.get<DeviceOptions[]>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/devices`, { params: body as any });
  }

  getScreensAndGroups(params: ActiveScreenOrGroupParameters): Observable<ScreenOrGroupListResponse> {
    return this.http.post<ScreenOrGroupListResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/screens-and-groups`, params);
  }

  getDevicesOnly(params: DevicesOnlyRequest): Observable<PaginationResult<IdName>> {
    return this.http.post<PaginationResult<IdName>>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/devices-only`, params);
  }

  getStatisticsEngagements(engagementRequest: EngagementRequest): Observable<EngagementResponse> {
    return this.http.post<EngagementResponse>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/engagements`, engagementRequest);
  }

  // FOR TESTING PURPOSES. Comment out when working with pdf generation
  // GET/Advertiser/send-pdf
  // sendPdf(): Observable<any> {
  //   return this.http.get<any>(`${this.rootUrl}/advertiser/send-pdf`);
  // }

  getCustomerSendouts(customerId: number): Observable<CampaignEmailSendout> {
    return this.http.get<CampaignEmailSendout>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/exposures-email-sendout`, { params: { customerId } });
  }

  createEmailSendout(emailSendout: CampaignEmailSendout): Observable<number> {
    return this.http.post<number>(
      `${this.rootUrl}${StatisticsApi.GetStatisticsPath}/exposures-email-sendout`,
      emailSendout
    );
  }

  updateEmailSendout(emailSendout: CampaignEmailSendout): Observable<number> {
    return this.http.put<number>(
      `${this.rootUrl}${StatisticsApi.GetStatisticsPath}/exposures-email-sendout`,
      emailSendout
    );
  }

  sendCampaignSendout(params: ManualSendoutRequest): Observable<void> {
    return this.http.post<void>(`${this.rootUrl}${StatisticsApi.GetStatisticsPath}/exposures-send-out-data`, params);
  }
}
